import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';
import { faFolder } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton } from '@mui/material';
import { clsx } from 'clsx';
import { Menu } from '../view-models/library.viewMappers';
import { bgColorsVars } from '@design-system/vars.css';
import { GalleryItemMenu } from './GalleryItemMenu';
import { useGallerySelection } from '../hooks/useGallerySelection';

export type FolderGalleryItem = {
  id: number;
  name: string;
  onClick: (multiselect: boolean) => void;
  onDoubleClick: () => void;
  selected: boolean;
  menu: Menu;
};
export type FoldersGallery = Array<FolderGalleryItem>;

export const FoldersGalleryView = ({
  data,
  loading,
  clearSelection,
  filesGalleryRef,
  foldersGalleryRef,
}: {
  data: FoldersGallery;
  loading: boolean;
  clearSelection: () => void;
  filesGalleryRef: React.MutableRefObject<HTMLDivElement | null>;
  foldersGalleryRef: React.MutableRefObject<HTMLDivElement | null>;
}) => {
  const { multiselect } = useGallerySelection({
    clearSelection,
    filesGalleryRef,
    foldersGalleryRef,
  });

  if (loading) {
    return (
      <div className="flex flex-wrap gap-6 max-w-[1200px] items-center justify-center">
        {Array(8)
          .fill(0)
          .map((_, i) => (
            <Skeleton
              height={260}
              sx={{ backgroundColor: bgColorsVars['N-20'], minWidth: 240 }}
              key={i}
            />
          ))}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col md:flex-row gap-6 flex-wrap items-center md:items-start mb-8"
      ref={foldersGalleryRef}
    >
      {data.map((item) => (
        <div
          key={item.id}
          className={clsx(
            'flex items-center justify-between border-[2px] hover:shadow-galleryHover rounded-[12px] p-4 h-[44px] bg-white gap-4 relative min-w-full md:min-w-min',
            item.selected && 'shadow-gallery',
            item.selected && sprinkles({ borderColor: 'P-80' }),
            !item.selected && sprinkles({ borderColor: 'W-100' }),
          )}
          onClick={() => {
            item.onClick(multiselect);
          }}
        >
          <div
            className="flex gap-2 items-center cursor-pointer"
            onDoubleClick={item.onDoubleClick}
          >
            <FontAwesomeIcon icon={faFolder} />
            <Typography
              variant="body"
              size="large"
              className="w-full md:w-[110px] overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {item.name}
            </Typography>
          </div>
          <GalleryItemMenu menu={item.menu} />
        </div>
      ))}
    </div>
  );
};
